import {useProjectId} from "../shopdrawing/ProjectName";
import {useAlternative} from "../quote/alternative/Alternative";
import {useEffect, useState} from "react";
import {
    Accordion, AccordionDetails, AccordionSummary,
    DialogActions, DialogTitle, Button, Divider, List, ListItemButton, ListItemText,
    Tab, Tabs, Typography, Chip, Alert
} from "@mui/material";
import {Dialog2} from "../../../misc/Dialog2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../../api/API";
import {
    RecalcHistoryItem,
    RecalcOpeningChange,
    RecalcPriceChangeItem,
    RecalcProductChangeItem,
} from "../../../api/Products";
import {makeProductChangeLines} from "../AlertProductRecalculationRequired";
import {green, red} from "@mui/material/colors";
import {CentsCol, NumberCol, RenderStringCol, StringCol, Table} from "../../../misc/scroller/Table";
import {PopperZIndexProvider} from "../../../misc/scroller/PopperZIndexProvider";
import moment from "moment";

export function HistoryDialog(props: {
    onClose(): void;
}) {
    const project = useProjectId();
    const alternative = useAlternative();

    const history = useAsync2(
        (input) => api.products.listRecalcHistory(input),
        {project, alternative},
        [project, alternative],
    );

    const [selected, setSelected] = useState<RecalcHistoryItem | null>(null);

    return (
        <Dialog2 open onClose={props.onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Product Change History</DialogTitle>
            <div style={{display: "flex", minHeight: 400, maxHeight: "60vh"}}>
                <div style={{
                    width: 240,
                    minWidth: 240,
                    borderRight: "1px solid rgba(0,0,0,0.12)",
                    overflowY: "auto",
                }}>
                    {history.LoadingOrErrorElement}
                    <List dense disablePadding>
                        {history.asList.map(item => (
                            <ListItemButton
                                key={item.id}
                                selected={selected?.id === item.id}
                                onClick={() => setSelected(item)}
                            >
                                <ListItemText
                                    primary={"#" + item.id + " - " + moment.utc(item.createdAt).format("MMM D, YYYY h:mm A")}
                                    secondary={<div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}>
                                        {item.createdByName || "System"}
                                        <Chip size="small" variant="outlined"
                                              label={item.successful ? "Success" : "Failed"}
                                              color={item.successful ? "success" : "error"}
                                        />
                                    </div>}
                                    primaryTypographyProps={{variant: "body2"}}
                                />
                            </ListItemButton>
                        ))}
                        {!history.loading && history.asList.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                                No history available.
                            </Typography>
                        )}
                    </List>
                </div>

                <div style={{flex: 1, display: "flex", flexDirection: "column", overflow: "hidden"}}>
                    {selected ? (
                        <HistoryDetail log={selected} />
                    ) : (
                        <div style={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center"}}>
                            <Typography color="text.secondary">Select an entry to view details</Typography>
                        </div>
                    )}
                </div>
            </div>
            <DialogActions>
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
        </Dialog2>
    );
}

function HistoryDetail(props: {log: RecalcHistoryItem}) {
    const [tab, setTab] = useState("products");
    const hasErr = !!props.log.errorText;

    useEffect(() => {
        if(hasErr) setTab("error");
        else setTab("products");
    }, [hasErr])

    return (
        <div style={{display: "flex", flexDirection: "column", flex: 1, overflow: "hidden"}}>

            {hasErr ? <>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Details" value="error" />
                </Tabs>
            </> : <>
                <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                    <Tab label="Product Changes" value="products" />
                    <Tab label="Price Changes" value="prices" />
                    <Tab label="Openings" value="openings" />
                </Tabs>
            </>}
            <Divider />
            <div style={{flex: 1, overflow: "auto", display: "flex", flexDirection: "column"}}>
                {tab === "products" && <ProductChanges logId={props.log.id} />}
                {tab === "prices" && <PriceChanges logId={props.log.id} />}
                {tab === "openings" && <OpeningChanges logId={props.log.id} />}
                {tab === "error" && <Alert severity="error">
                    {props.log.errorText}
                </Alert>}
            </div>
        </div>
    );
}

function ProductChanges(props: {logId: number}) {
    const data = useAsync2(
        (input) => api.products.listRecalcProductChanges(input),
        {log: props.logId},
        [props.logId],
    );

    return (
        <div style={{padding: 16}}>
            {data.LoadingOrErrorElement}
            {data.asList.length === 0 && !data.loading && (
                <Typography color="text.secondary">No product changes recorded.</Typography>
            )}
            {data.asList.map((item) => (
                <ProductChangeItem key={item.id} item={item} />
            ))}
        </div>
    );
}

function ProductChangeItem(props: {item: RecalcProductChangeItem}) {
    const {item} = props;

    return (
        <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1">
                    {item.categoryName} {item.productCode} {item.finishCode}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {item.changes.map((change, i) => {
                    const lines = makeProductChangeLines(change.meta);

                    return (
                        <div key={i}>
                            <table style={{width: "100%"}}>
                                <tbody>
                                    {lines.length === 0 && <tr><td>{change.description}</td></tr>}

                                    {lines.map((line, j) => (
                                        <tr key={j}>
                                            <td>{line.name}</td>
                                            <td style={{
                                                textDecoration: "line-through",
                                                color: red["800"],
                                            }}>{line.old}</td>
                                            <td style={{
                                                color: green["900"],
                                            }}>{line.new}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
}

function PriceChanges(props: {logId: number}) {
    return (
        <PopperZIndexProvider value={1400}>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <Table<RecalcPriceChangeItem>
                    name="recalc-history-prices"
                    columns={[
                        StringCol("Item", "itemName", 200),
                        StringCol("Type", "productType", 120),
                        NumberCol("Prev Qty", "prevQty", 80),
                        CentsCol("Prev Ext Price", "afterExtendedPrice", 120),
                        NumberCol("Qty", "afterQty", 80),
                        CentsCol("Ext Price", "afterExtendedPrice", 120),
                        RenderStringCol<RecalcPriceChangeItem>("Status", r => {
                            if (r.added) return "Added";
                            if (r.removed) return "Removed";
                            return "Updated";
                        }, 100),
                    ]}
                    cellCustomize={{
                        backgroundColor: (row: RecalcPriceChangeItem) => {
                            if (row.added) return green["50"];
                            if (row.removed) return red["50"];
                            return undefined;
                        },
                    }}
                    fetch={async () => {
                        return api.products.listRecalcPriceChanges({log: props.logId});
                    }}
                    fetchDeps={[props.logId]}
                />
            </div>
        </PopperZIndexProvider>
    );
}

function OpeningChanges(props: {logId: number}) {
    return (
        <PopperZIndexProvider value={1400}>
            <div style={{flex: 1, display: "flex", flexDirection: "column"}}>
                <Table<RecalcOpeningChange>
                    name="recalc-history-openings"
                    columns={[
                        StringCol("Opening", "name", 300),
                    ]}
                    fetch={async () => {
                        return api.products.listRecalcOpenings({log: props.logId});
                    }}
                    fetchDeps={[props.logId]}
                />
            </div>
        </PopperZIndexProvider>
    );
}
